import type { AdminFeatureFlag } from './api.js';

export interface FeatureFlagMeta {
  key: AdminFeatureFlag;
  label: string;
  help: string;
}

/** Shared labels for both the per-tenant controls and the fleet comparison matrix. */
export const FEATURE_FLAG_META: FeatureFlagMeta[] = [
  {
    key: 'noeBuildiumOnly',
    label: 'NOE Buildium-only',
    help: 'Notice-of-Entry skips the Email Draft channel — Buildium template only.',
  },
  {
    key: 'noeEmailOnly',
    label: 'NOE Email-only',
    help: 'Notice-of-Entry skips Buildium sends — Email NOE only.',
  },
  {
    key: 'noeCalendarPromptEnabled',
    label: 'NOE calendar prompt',
    help: 'After a NOE sends, ask the PM whether to add the entry window to Google Calendar.',
  },
  {
    key: 'noeEmailReminderEnabled',
    label: 'NOE email reminder',
    help: 'For email-channel NOEs, schedule a day-of courtesy reminder email.',
  },
  {
    key: 'invoicingEnabled',
    label: 'Invoicing',
    help: 'Invoice email intake + PM review cards for this tenant.',
  },
  {
    key: 'invoiceIntakeGateEnabled',
    label: 'Invoice intake gate',
    help: 'Holds only generation-2 email candidates with strong non-invoice evidence in the auditable Not an invoice tray. Default off.',
  },
  {
    key: 'invoiceLinkFetchEnabled',
    label: 'Invoice link fetch',
    help: "Off by default. On: when an invoice email has no attachment, Cortex keeps Square and QuickBooks invoice links and fetches the invoice PDF behind them (read-only GET, strict host allowlist checked after every redirect), then processes it like an attachment. Off: link-only emails keep today's screenshot fallback.",
  },
  {
    key: 'apTaskAutomationEnabled',
    label: 'AP1 task automation',
    help: 'Automatically matches owner-billable AP1 charges to Buildium tasks (or creates one when the property is unambiguous), and links one clearly matching open Rentvine work order.',
  },
  {
    key: 'apPostedUndoEnabled',
    label: 'AP2 undo to AP1',
    help: 'Allows unpaid posted parents to return to AP1 for repeatable sandbox scenarios. External PMS corrections stay manual.',
  },
  {
    key: 'apVendorCopyEmailEnabled',
    label: 'AP2 vendor-copy email',
    help: 'Allows primary owners and admins to email the exact filtered Vendor copy CSV. Requires AP2, Resend, and a configured vendor sender address.',
  },
  {
    key: 'apInvoiceReprocessEnabled',
    label: 'AP1 invoice reprocess',
    help: 'Shows the primary-owner-only Reprocess action for AP1 rows. Re-runs persisted invoice evidence without posting, task creation, Gmail, or sibling-row side effects.',
  },
  {
    key: 'internalTechInvoicesEnabled',
    label: 'Internal-worker labor invoices',
    help: 'Route PDF invoices from a roster technician ($0 vendor cost) into tech billing: charge = hours × the worker’s configured rate, with the owner charge from the two-rate model.',
  },
  {
    key: 'techHelperLaborEnabled',
    label: 'Tech helper labor lines',
    help: 'Two-person jobs: the primary technician’s report may name a helper with their own hours. AP1 stays one charge; posting fans out one payable per technician. Gates creation only — existing multi-line rows keep working when toggled off.',
  },
  {
    key: 'technicianSheetStatusWritebackEnabled',
    label: 'Technician-sheet pay/status writeback',
    help: 'Mirrors current AP technician pay and status into connected Google Sheets. Activate from the tenant Configuration dashboard after every route validates; this control remains available for emergency disable.',
  },
  {
    key: 'technicianSheetRowProjectionEnabled',
    label: 'Technician-sheet row projection',
    help: 'Current-truth Work and Reimbursement rows. Read-only here; use the guarded activation command so legacy H:I swaps atomically.',
  },
  {
    key: 'configureSelfServeEnabled',
    label: 'Self-serve Configure',
    help: 'Primary owner can edit company identity, Buildium account IDs, and safe feature toggles on the tenant dashboard Configuration page.',
  },
  {
    key: 'taskProgressReportIntakeEnabled',
    label: 'Task Progress Report intake',
    help: 'Generic TPR form normalizer. Subject patterns, route type, technician/vendor mappings, and form labels are configured per tenant by ops.',
  },
  {
    key: 'seasonalInspectionIntakeEnabled',
    label: 'Seasonal inspection intake',
    help: 'Seasonal inspection form normalizer. Subject prefix, John vendor mapping, form labels, and rates are configured per tenant by ops.',
  },
  {
    key: 'etransferForwarderDkimRequired',
    label: 'Require DKIM for e-transfer forwards',
    help: 'On by default. Off skips aligned DKIM for this client’s verified forwards, including Gmail and Bluehost. SPF has a separate requirement. Sender, recipient, provider and DMARC rules still apply. Disabling authentication reduces spoofing protection. Direct bank mail stays strict.',
  },
  {
    key: 'etransferForwarderSpfRequired',
    label: 'Require SPF for e-transfer forwards',
    help: 'On by default. Off skips matching SPF for this client’s verified forwards. DKIM has a separate requirement. Sender, recipient, provider and DMARC rules still apply. Disabling both reduces spoofing protection even for a verified address. Direct bank mail stays strict.',
  },
  {
    key: 'etransferSignedForwardingEnabled',
    label: 'Gmail and Bluehost e-transfer forwarding',
    help: 'Off by default. Requires a staged managed mailbox and verified sender. Allows Gmail, Bluehost and Microsoft 365 forwards subject to the client’s DKIM and SPF requirements; an absent DMARC result is allowed, but a failing result is rejected. Changing this resets delivery-policy verification. Automatic posting also requires the auto-post flag and a confirmed payer/lease match.',
  },
  {
    key: 'etransfersEnabled',
    label: 'E-transfers',
    help: 'Gmail e-transfer intake, Telegram approval queue, and rent-payment posting.',
  },
  {
    key: 'etransferTelegramNotificationsEnabled',
    label: 'E-transfer Telegram notices',
    help: 'On by default for every client. Sends outcome notices for accepted receipts and confirmed posts. Off stops those outcome notices. Approve and ignore review cards still send, and an ambiguous post stays the retryable failed review instead of an unknown hold. Intake and automatic posting stay unchanged.',
  },
  {
    key: 'etransferAutoPostEnabled',
    label: 'E-transfer auto-post',
    help: 'Posts only future e-transfers from previously approved trusted payer identities. First-time, ambiguous, conflicted, or unconfigured payments stay manual.',
  },
  {
    key: 'etransferShadowModeEnabled',
    label: 'E-transfer shadow mode',
    help: 'Pilot mode: ingest, match, write the ledger, and build trusted payer links without posting to Buildium.',
  },
  {
    key: 'etransferPaymentUndoEnabled',
    label: 'E-transfer undo',
    help: 'Primary-owner action to reverse a posted Buildium e-transfer payment and return it to manual review for reposting.',
  },
  {
    key: 'etransferOwnerContributionPostingEnabled',
    label: 'E-transfer owner contributions',
    help: 'Posts confirmed owner contributions to Buildium. Enabling requires e-transfer intake on, shadow mode off, the Buildium provider, and a positive owner-contribution offset GL account.',
  },
  {
    key: 'gmailReconnectBackfillEnabled',
    label: 'Gmail reconnect backfill',
    help: 'After a paused Gmail lane reconnects, backfill its bounded outage gap for invoice and e-transfer intake.',
  },
  {
    key: 'legalTestMode',
    label: 'Legal test mode',
    help: 'Demo/testing only: N1 skips the Buildium write-back, N4 sends to the test email, and both bypass dedup so the flow re-runs cleanly.',
  },
  {
    key: 'laneMaintenanceEnabled',
    label: 'Maintenance lane (client dashboard)',
    help: 'Display-only: when off, the client dashboard shows Maintenance locked (greyed, not clickable, with an upgrade prompt on its lane card, pie and page). Does not disable maintenance automation.',
  },
  {
    key: 'laneAccountingEnabled',
    label: 'Accounting lane (client dashboard)',
    help: 'Display-only: when off, the client dashboard shows Accounting locked (greyed, not clickable, with an upgrade prompt on its lane card, pie and page). Does not disable invoice or accounting automation.',
  },
  {
    key: 'laneLeasingEnabled',
    label: 'Leasing lane (client dashboard)',
    help: 'Off by default: the client dashboard shows Leasing locked (greyed, not clickable, with an upgrade prompt on its lane card, pie and page). Turn on to unlock Leasing, starting with rental application analysis. Does not disable leasing workflows.',
  },
  {
    key: 'accountingProfitabilityEnabled',
    label: 'Accounting profitability (client dashboard)',
    help: 'On by default: shows Explore profitability and balances on Accounting (read-only; nothing is posted or paid). Turn off to hide it for this client. It only shows while the fleet-wide ACCOUNTING_PROFITABILITY_ENABLED switch is on.',
  },
  {
    key: 'homeKpiLinksEnabled',
    label: 'Tappable Home number tiles (client dashboard)',
    help: 'On by default: on screens wider than a phone, the Automation pulse, Tasks today, Total AP and Gross profit tiles on Home open Agent Performance, Maintenance, Accounting and Accounting profitability (Last 30 days). Turn off to keep them as plain numbers for this client.',
  },
  {
    key: 'vendorContactColumnsEnabled',
    label: 'Vendor contact columns',
    help: 'Shows editable vendor Email and Phone columns, requires at least one contact, and matches AP invoice senders to vendor email only.',
  },
];
